import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { FaAngleLeft, FaWhatsapp } from "react-icons/fa";
import { useAuth } from "context/AuthContext";
import { useUser } from "context/UserContext";
import { useLanguage } from "context/LanguageContext";
import {
  PASSWORD_CHANGE_ILLUSTRATION_URL,
  EMAIL_CHANGE_ILLUSTRATION_URL,
  PHONE_CHANGE_ILLUSTRATION_URL,
  ACCOUNT_BANK_ILLUSTRATION_URL,
} from "./illustrations";
import { BANCO_OPTIONS, restriccionesPorBanco } from "./bankConstants";
import "./Ajustes.css";

function IconEye() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function completarConCeros(numero, longitudRequerida) {
  return numero.padStart(longitudRequerida, "0");
}

function filtrarSoloNumeros(valor) {
  return valor.replace(/[^0-9]/g, "");
}

function isEmptyAccountDisplay(banco, numero) {
  const b = (banco || "").trim();
  const n = (numero || "").trim();
  if (!b && !n) return true;
  if (!b || !n) return true;
  if (b.toUpperCase() === "N/A" || n.toUpperCase() === "N/A") return true;
  return false;
}

/** Celular Ecuador: 09 + 7 dígitos */
const PHONE_EC_REGEX = /^09\d{7}$/;

function validarNumeroCuenta(num, bancoVal, isEnglish) {
  if (!bancoVal || !num) return { ok: false, msg: "" };
  const { maxLength } = restriccionesPorBanco(bancoVal);
  if (num.length !== maxLength) {
    return {
      ok: false,
      msg: isEnglish
        ? `Account number must be exactly ${maxLength} digits.`
        : `El número debe tener exactamente ${maxLength} dígitos.`,
    };
  }
  return { ok: true, msg: "" };
}

const COPY = {
  es: {
    backAjustes: "Volver a ajustes",
    confirm: "Confirmar",
    confirmSend: "Confirmar",
    sending: "Enviando…",
    needHelp: "¿Necesitas ayuda?",
    legalPersonal: "Solo cuenta personal a tu nombre.",
    bankCurrent: "Cuenta bancaria actual",
    bankNotSpecified: "No especificado",
    selectBank: "Selecciona un banco",
    accountNumber: "Número de cuenta",
    confirmAccountNumber: "Confirmar número de cuenta",
    currentEmail: "Email actual",
    currentPhone: "Celular actual",
    newEmail: "Nuevo email",
    confirmEmail: "Confirmar nuevo email",
    newPassword: "Nueva contraseña",
    confirmPassword: "Confirmar nueva contraseña",
    phoneHint: "9 dígitos, debe empezar con 09.",
    phonePlaceholder: "09xxxxxxx",
    pwdHint:
      "Te recomendamos al menos 8 caracteres con mayúsculas, minúsculas y números.",
    pwdSuccess: "Contraseña cambiada con éxito",
    modalTitle: "¿Confirmar el cambio?",
    modalCancel: "Cancelar",
    modalConfirmBtn: "Sí, confirmar",
    modalPasswordBody: "Tu contraseña se actualizará de inmediato.",
    modalEmailBody: "Enviaremos una solicitud para cambiar tu email.",
    modalPhoneBody: "Tu celular se actualizará de inmediato.",
    modalAccountBody: "Enviaremos una solicitud para actualizar tu cuenta bancaria.",
    emailSuccessTitle: "Email actualizado",
    emailSuccessBody: "Tu email se actualizó con éxito.",
    phoneSuccessTitle: "Celular actualizado",
    phoneSuccessBody: "Tu celular se actualizó con éxito.",
    accountSuccessTitle: "Cuenta actualizada",
    accountSuccessBody: "Tu cuenta bancaria se actualizó con éxito.",
    titlePassword: "Cambiar contraseña",
    titleEmail: "Cambiar email",
    titlePhone: "Cambiar celular",
    titleAccount: "Cambiar cuenta bancaria",
    errShortPwd: "La contraseña debe tener al menos 5 caracteres.",
    errPwdMatch: "Las contraseñas no coinciden.",
    errEmail: "Email no válido.",
    errEmailMatch: "Los emails no coinciden.",
    errPhone: "Revisa el formato del celular.",
    errPhoneMatch: "Los números no coinciden.",
    errSamePhone: "Debe ser distinto al actual.",
    errSameEmail: "Debe ser distinto al actual.",
    errGeneric: "No se pudo completar. Intenta de nuevo.",
    errBank: "Revisa banco y número de cuenta.",
    errAccountMatch: "Los números de cuenta no coinciden.",
  },
  en: {
    backAjustes: "Back to settings",
    confirm: "Confirm",
    confirmSend: "Confirm",
    sending: "Sending…",
    needHelp: "Need help?",
    legalPersonal: "Personal account in your name only.",
    bankCurrent: "Current bank account",
    bankNotSpecified: "Not specified",
    selectBank: "Select a bank",
    accountNumber: "Account number",
    confirmAccountNumber: "Confirm account number",
    currentEmail: "Current email",
    currentPhone: "Current phone",
    newEmail: "New email",
    confirmEmail: "Confirm new email",
    newPassword: "New password",
    confirmPassword: "Confirm new password",
    phoneHint: "9 digits, must start with 09.",
    phonePlaceholder: "09xxxxxxx",
    pwdHint: "We recommend 8+ characters with upper, lower case and numbers.",
    pwdSuccess: "Password changed successfully",
    modalTitle: "Confirm this change?",
    modalCancel: "Cancel",
    modalConfirmBtn: "Yes, confirm",
    modalPasswordBody: "Your password will be updated immediately.",
    modalEmailBody: "We will send a request to change your email.",
    modalPhoneBody: "Your phone number will be updated immediately.",
    modalAccountBody: "We will send a request to update your bank account.",
    emailSuccessTitle: "Email updated",
    emailSuccessBody: "Your email was updated successfully.",
    phoneSuccessTitle: "Phone updated",
    phoneSuccessBody: "Your phone number was updated successfully.",
    accountSuccessTitle: "Account updated",
    accountSuccessBody: "Your bank account was updated successfully.",
    titlePassword: "Change password",
    titleEmail: "Change email",
    titlePhone: "Change phone",
    titleAccount: "Change bank account",
    errShortPwd: "Password must be at least 5 characters.",
    errPwdMatch: "Passwords do not match.",
    errEmail: "Invalid email.",
    errEmailMatch: "Emails do not match.",
    errPhone: "Check phone format.",
    errPhoneMatch: "Numbers do not match.",
    errSamePhone: "Must differ from current.",
    errSameEmail: "Must differ from current.",
    errGeneric: "Could not complete. Try again.",
    errBank: "Check bank and account number.",
    errAccountMatch: "Account numbers do not match.",
  },
};

function bankHelpText(banco, isEn) {
  const { maxLength } = restriccionesPorBanco(banco);
  if (isEn) {
    return `${maxLength} digits, pad with zeros on the left if needed.`;
  }
  return `${maxLength} dígitos, rellenando con ceros a la izquierda si aplica.`;
}

export default function AjustesChangeScreen({ screen, embedded = false, onClose }) {
  const navigate = useNavigate();
  const { user, loading: authLoading, getToken } = useAuth();
  const { isEn } = useLanguage();
  const lang = isEn ? "en" : "es";
  const t = useMemo(() => COPY[lang], [lang]);

  const {
    email,
    celular,
    banco: bancoCtx,
    numeroCuenta: numeroCtx,
    nombre,
    apellido,
    isDataLoaded,
    updateLocalData,
  } = useUser();

  const baseURL = process.env.REACT_APP_BASE_URL;

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  /* password */
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPwd, setShowPwd] = useState({ a: false, b: false });
  /** null | 'password' | 'email' | 'phone' | 'account' */
  const [confirmModalFor, setConfirmModalFor] = useState(null);
  const [passwordChanged, setPasswordChanged] = useState(false);

  /* email */
  const [newEmail, setNewEmail] = useState("");
  const [confirmMail, setConfirmMail] = useState("");
  const [emailDone, setEmailDone] = useState(false);

  /* phone */
  const [newPhone, setNewPhone] = useState("");
  const [confirmPhone, setConfirmPhone] = useState("");
  const [phoneDone, setPhoneDone] = useState(false);

  /* account */
  const [banco, setBanco] = useState("");
  const [numeroCuenta, setNumeroCuenta] = useState("");
  const [numeroCuenta2, setNumeroCuenta2] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [accountDone, setAccountDone] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/authentication/sign-in");
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    const onDoc = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (isDataLoaded && screen === "account") {
      if (bancoCtx) setBanco(bancoCtx);
      if (numeroCtx) {
        setNumeroCuenta(numeroCtx);
        setNumeroCuenta2(numeroCtx);
      }
    }
  }, [isDataLoaded, bancoCtx, numeroCtx, screen]);

  useEffect(() => {
    if (!confirmModalFor || submitting) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setConfirmModalFor(null);
        setFormError("");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirmModalFor, submitting]);

  const finishOrLeave = useCallback(() => {
    if (embedded && typeof onClose === "function") {
      onClose();
    } else {
      navigate("/ajustes");
    }
  }, [embedded, onClose, navigate]);

  const putProfesional = useCallback(
    async (body) => {
      const token = await getToken();
      if (!token) {
        navigate("/authentication/sign-in");
        throw new Error("no token");
      }
      const profesionalId = localStorage.getItem("id");
      if (!profesionalId) throw new Error("no id");
      const res = await fetch(`${baseURL}/actualizar-profesional/${profesionalId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      return res;
    },
    [baseURL, getToken, navigate]
  );

  const handleBack = () => {
    if (confirmModalFor) {
      setConfirmModalFor(null);
      setFormError("");
      return;
    }
    if (screen === "password") {
      if (passwordChanged) {
        finishOrLeave();
        return;
      }
      finishOrLeave();
      return;
    }
    if (emailDone || phoneDone || accountDone) {
      finishOrLeave();
      return;
    }
    finishOrLeave();
  };

  const pwdTooShort = newPassword.length > 0 && newPassword.length < 5;
  const pwdMismatch =
    newPassword.length >= 5 && confirmPassword.length > 0 && newPassword !== confirmPassword;
  const pwdOk =
    newPassword.length >= 5 && newPassword === confirmPassword && newPassword.length > 0;

  const onPasswordPrimary = () => {
    setFormError("");
    if (newPassword.length < 5) {
      setFormError(t.errShortPwd);
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError(t.errPwdMatch);
      return;
    }
    setFormError("");
    setConfirmModalFor("password");
  };

  const emailValid = EMAIL_RE.test(newEmail) && EMAIL_RE.test(confirmMail);
  const emailMatch = newEmail === confirmMail;
  const emailDiff = newEmail && newEmail !== (email || "");

  const requestSubmitEmail = () => {
    setFormError("");
    if (!emailValid) {
      setFormError(t.errEmail);
      return;
    }
    if (!emailMatch) {
      setFormError(t.errEmailMatch);
      return;
    }
    if (!emailDiff) {
      setFormError(t.errSameEmail);
      return;
    }
    setConfirmModalFor("email");
  };

  const phoneOk =
    PHONE_EC_REGEX.test(newPhone) &&
    PHONE_EC_REGEX.test(confirmPhone) &&
    newPhone === confirmPhone &&
    newPhone !== (celular || "");

  const requestSubmitPhone = () => {
    setFormError("");
    if (!phoneOk) {
      setFormError(t.errPhone);
      return;
    }
    setConfirmModalFor("phone");
  };

  const requestSubmitAccount = () => {
    setFormError("");
    if (!banco) {
      setFormError(t.errBank);
      return;
    }
    if (numeroCuenta !== numeroCuenta2) {
      setFormError(t.errAccountMatch);
      return;
    }
    const v = validarNumeroCuenta(numeroCuenta, banco, isEn);
    if (!v.ok) {
      setFormError(v.msg || t.errBank);
      return;
    }
    setConfirmModalFor("account");
  };

  const performPasswordChange = useCallback(async () => {
    setSubmitting(true);
    setFormError("");
    try {
      const res = await putProfesional({ contraseña: newPassword });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || t.errGeneric);
      }
      setPasswordChanged(true);
      setConfirmModalFor(null);
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      setFormError(e.message || t.errGeneric);
    } finally {
      setSubmitting(false);
    }
  }, [newPassword, putProfesional, t.errGeneric]);

  const performEmailChange = useCallback(async () => {
    if (!EMAIL_RE.test(newEmail) || !EMAIL_RE.test(confirmMail) || newEmail !== confirmMail || newEmail === (email || "")) {
      setFormError(t.errEmail);
      setConfirmModalFor(null);
      return;
    }
    setSubmitting(true);
    setFormError("");
    try {
      const res = await putProfesional({ email: newEmail });
      if (!res.ok) throw new Error(t.errGeneric);
      updateLocalData({ email: newEmail });
      setEmailDone(true);
      setConfirmModalFor(null);
      setNewEmail("");
      setConfirmMail("");
    } catch {
      setFormError(t.errGeneric);
    } finally {
      setSubmitting(false);
    }
  }, [newEmail, confirmMail, email, putProfesional, t.errEmail, t.errGeneric, updateLocalData]);

  const performPhoneChange = useCallback(async () => {
    if (
      !PHONE_EC_REGEX.test(newPhone) ||
      !PHONE_EC_REGEX.test(confirmPhone) ||
      newPhone !== confirmPhone ||
      newPhone === (celular || "")
    ) {
      setFormError(t.errPhone);
      setConfirmModalFor(null);
      return;
    }
    setSubmitting(true);
    setFormError("");
    try {
      const res = await putProfesional({ celular: newPhone });
      if (!res.ok) throw new Error(t.errGeneric);
      updateLocalData({ celular: newPhone });
      setPhoneDone(true);
      setConfirmModalFor(null);
      setNewPhone("");
      setConfirmPhone("");
    } catch {
      setFormError(t.errGeneric);
    } finally {
      setSubmitting(false);
    }
  }, [newPhone, confirmPhone, celular, putProfesional, t.errPhone, t.errGeneric, updateLocalData]);

  const performAccountChange = useCallback(async () => {
    setFormError("");
    if (!banco) {
      setFormError(t.errBank);
      setConfirmModalFor(null);
      return;
    }
    if (numeroCuenta !== numeroCuenta2) {
      setFormError(t.errAccountMatch);
      setConfirmModalFor(null);
      return;
    }
    const v = validarNumeroCuenta(numeroCuenta, banco, isEn);
    if (!v.ok) {
      setFormError(v.msg || t.errBank);
      setConfirmModalFor(null);
      return;
    }
    const restr = restriccionesPorBanco(banco);
    const numeroCompletado = completarConCeros(numeroCuenta, restr.maxLength);
    setSubmitting(true);
    try {
      const res = await putProfesional({ banco, numeroCuenta: numeroCompletado });
      if (!res.ok) throw new Error(t.errGeneric);
      updateLocalData({ banco, numeroCuenta: numeroCompletado });
      setAccountDone(true);
      setConfirmModalFor(null);
    } catch {
      setFormError(t.errGeneric);
    } finally {
      setSubmitting(false);
    }
  }, [banco, numeroCuenta, numeroCuenta2, putProfesional, t.errAccountMatch, t.errBank, t.errGeneric, updateLocalData, isEn]);

  const onModalConfirm = useCallback(() => {
    if (confirmModalFor === "password") void performPasswordChange();
    else if (confirmModalFor === "email") void performEmailChange();
    else if (confirmModalFor === "phone") void performPhoneChange();
    else if (confirmModalFor === "account") void performAccountChange();
  }, [confirmModalFor, performPasswordChange, performEmailChange, performPhoneChange, performAccountChange]);

  const confirmModalBody = useMemo(() => {
    switch (confirmModalFor) {
      case "password":
        return t.modalPasswordBody;
      case "email":
        return t.modalEmailBody;
      case "phone":
        return t.modalPhoneBody;
      case "account":
        return t.modalAccountBody;
      default:
        return "";
    }
  }, [confirmModalFor, t]);

  const closeConfirmModal = useCallback(() => {
    if (submitting) return;
    setConfirmModalFor(null);
    setFormError("");
  }, [submitting]);

  const openWhatsApp = () => {
    const phoneNumber = "59895438303";
    const userName = `${nombre || ""} ${apellido || ""}`.trim() || "Usuario";
    const empresa = "PayFlex";
    const msg = isEn
      ? `Hi! I'm ${userName}, I need help changing my bank account at ${empresa}.`
      : `Hola! Soy ${userName}, necesito ayuda para cambiar mi número de cuenta bancaria en ${empresa}.`;
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const title = useMemo(() => {
    if (screen === "password") return t.titlePassword;
    if (screen === "email") return t.titleEmail;
    if (screen === "phone") return t.titlePhone;
    return t.titleAccount;
  }, [screen, t]);

  const renderBody = () => {
    if (screen === "password") return renderPassword();
    if (screen === "email") return renderEmail();
    if (screen === "phone") return renderPhone();
    return renderAccount();
  };

  const illustration = useMemo(() => {
    if (screen === "password") return PASSWORD_CHANGE_ILLUSTRATION_URL;
    if (screen === "email") return EMAIL_CHANGE_ILLUSTRATION_URL;
    if (screen === "phone") return PHONE_CHANGE_ILLUSTRATION_URL;
    return ACCOUNT_BANK_ILLUSTRATION_URL;
  }, [screen]);

  const illustrationWrapClass = useMemo(() => {
    if (screen === "account") return "AjustesChangeIllustrationWrap AjustesChangeIllustrationWrap--account";
    if (screen === "email") return "AjustesChangeIllustrationWrap AjustesChangeIllustrationWrap--email";
    if (screen === "phone") return "AjustesChangeIllustrationWrap AjustesChangeIllustrationWrap--phone";
    return "AjustesChangeIllustrationWrap";
  }, [screen]);

  const contentGridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr",
    maxWidth: 560,
    margin: "0 auto",
    width: "100%",
  };

  /* ---------- PASSWORD ---------- */
  const renderPassword = () => {
    if (passwordChanged) {
      return renderSuccessBlock(t.pwdSuccess, null);
    }
    return (
      <>
        <div className="AjustesChangePasswordIntro">
          <img
            src={illustration}
            alt=""
            className="AjustesChangeIntroIllustration"
            loading="lazy"
            decoding="async"
          />
          <p className="AjustesChangePasswordHint">{t.pwdHint}</p>
        </div>
        <div className="AjustesChangeNarrowStack">
          <div className="PasswordInputWrapper">
            <input
              className={`PasswordInput ${pwdTooShort ? "ErrorBorder" : ""}`}
              type={showPwd.a ? "text" : "password"}
              placeholder={t.newPassword}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
            />
            <button type="button" className="TogglePasswordIcon" onClick={() => setShowPwd((s) => ({ ...s, a: !s.a }))}>
              <span className={`icon-container ${showPwd.a ? "" : "crossed"}`}>
                <IconEye />
              </span>
            </button>
          </div>
          {pwdTooShort ? <p className="ErrorText">{t.errShortPwd}</p> : null}
          <div className="PasswordInputWrapper">
            <input
              className={`PasswordInput ${pwdMismatch ? "ErrorBorder" : ""}`}
              type={showPwd.b ? "text" : "password"}
              placeholder={t.confirmPassword}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
            <button type="button" className="TogglePasswordIcon" onClick={() => setShowPwd((s) => ({ ...s, b: !s.b }))}>
              <span className={`icon-container ${showPwd.b ? "" : "crossed"}`}>
                <IconEye />
              </span>
            </button>
          </div>
          {pwdMismatch ? <p className="ErrorText">{t.errPwdMatch}</p> : null}
          {formError && !confirmModalFor ? <p className="ErrorText">{formError}</p> : null}
          <div className="AjustesChangeActions">
            <button type="button" className="PrimaryButton" disabled={submitting || !!confirmModalFor || !pwdOk} onClick={onPasswordPrimary}>
              {t.confirm}
            </button>
          </div>
        </div>
      </>
    );
  };

  const renderSuccessBlock = (headline, body) => (
    <div className="AjustesChangeSuccessWrap">
      <div className="AjustesChangeSuccessMark" aria-hidden>
        <span className="AjustesChangeSuccessMarkCircle">
          <svg viewBox="0 0 24 24" className="AjustesChangeSuccessMarkIcon" aria-hidden focusable="false">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
          </svg>
        </span>
      </div>
      <p className="AjustesChangeSuccessMessage">{headline}</p>
      {body ? <p className="AjustesChangeSuccessSub">{body}</p> : null}
      <div className="AjustesChangeActions">
        <button type="button" className="PrimaryButton" onClick={finishOrLeave}>
          {t.backAjustes}
        </button>
      </div>
    </div>
  );

  const renderEmail = () => {
    if (emailDone) {
      return renderSuccessBlock(t.emailSuccessTitle, t.emailSuccessBody);
    }
    return (
      <div className="AjustesChangeNarrowStack">
        <div className={illustrationWrapClass}>
          <img
            src={illustration}
            alt=""
            className="AjustesChangeIntroIllustration"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="AjustesChangeCurrentBlock">
          <p className="AjustesChangeCurrentLabel">{t.currentEmail}</p>
          <p className="AjustesChangeCurrentValue">{email || "—"}</p>
        </div>
        <div className="PasswordInputWrapper">
          <input
            className={`PasswordInput ${formError && newEmail && !confirmModalFor ? "ErrorBorder" : ""}`}
            type="email"
            placeholder={t.newEmail}
            value={newEmail}
            onChange={(e) => {
              setNewEmail(e.target.value);
              setFormError("");
            }}
          />
        </div>
        <div className="PasswordInputWrapper">
          <input
            className="PasswordInput"
            type="email"
            placeholder={t.confirmEmail}
            value={confirmMail}
            onChange={(e) => {
              setConfirmMail(e.target.value);
              setFormError("");
            }}
          />
        </div>
        {formError && !confirmModalFor ? <p className="ErrorText">{formError}</p> : null}
        <div className="AjustesChangeActions">
          <button
            type="button"
            className="PrimaryButton"
            disabled={submitting || !!confirmModalFor || !emailValid || !emailMatch || !emailDiff}
            onClick={requestSubmitEmail}
          >
            {submitting ? t.sending : t.confirmSend}
          </button>
        </div>
      </div>
    );
  };

  const renderPhone = () => {
    if (phoneDone) {
      return renderSuccessBlock(t.phoneSuccessTitle, t.phoneSuccessBody);
    }
    const onPhoneChange = (setter) => (e) => {
      const v = filtrarSoloNumeros(e.target.value).slice(0, 9);
      setter(v);
      setFormError("");
    };
    return (
      <div className="AjustesChangeNarrowStack">
        <div className={illustrationWrapClass}>
          <img
            src={illustration}
            alt=""
            className="AjustesChangeIntroIllustration"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="AjustesChangeCurrentBlock">
          <p className="AjustesChangeCurrentLabel">{t.currentPhone}</p>
          <p className="AjustesChangeCurrentValue">{celular || "—"}</p>
        </div>
        <div className="PasswordInputWrapper">
          <input
            className={`PasswordInput ${formError && !confirmModalFor ? "ErrorBorder" : ""}`}
            type="tel"
            inputMode="numeric"
            maxLength={9}
            placeholder={t.phonePlaceholder}
            value={newPhone}
            onChange={onPhoneChange(setNewPhone)}
          />
        </div>
        <div className="PasswordInputWrapper">
          <input
            className="PasswordInput"
            type="tel"
            inputMode="numeric"
            maxLength={9}
            placeholder={t.phonePlaceholder}
            value={confirmPhone}
            onChange={onPhoneChange(setConfirmPhone)}
          />
        </div>
        {!formError || confirmModalFor ? (
          <p style={{ fontSize: 12, color: "#666", textAlign: "center", margin: "0 0 8px" }}>{t.phoneHint}</p>
        ) : null}
        {formError && !confirmModalFor ? <p className="ErrorText">{formError}</p> : null}
        <div className="AjustesChangeActions">
          <button type="button" className="PrimaryButton" disabled={submitting || !!confirmModalFor || !phoneOk} onClick={requestSubmitPhone}>
            {submitting ? t.sending : t.confirmSend}
          </button>
        </div>
      </div>
    );
  };

  const renderAccount = () => {
    if (accountDone) {
      return renderSuccessBlock(t.accountSuccessTitle, t.accountSuccessBody);
    }
    const selectedLabel = BANCO_OPTIONS.find((o) => o.value === banco)?.label || "";
    const v = validarNumeroCuenta(numeroCuenta, banco, isEn);
    const canSubmit =
      banco &&
      numeroCuenta &&
      numeroCuenta2 &&
      numeroCuenta === numeroCuenta2 &&
      v.ok &&
      !submitting;

    return (
      <div className="AjustesChangeNarrowStack">
        <div className={illustrationWrapClass}>
          <img
            src={illustration}
            alt=""
            className="AjustesChangeIntroIllustration"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="AjustesChangeCurrentBlock">
          <p className="AjustesChangeCurrentLabel">{t.bankCurrent}</p>
          <p className="AjustesChangeCurrentValue">
            {isEmptyAccountDisplay(bancoCtx, numeroCtx) ? t.bankNotSpecified : `${bancoCtx} - ${numeroCtx}`}
          </p>
        </div>
        <div className="CustomSelectWrapper" ref={dropdownRef}>
          <div
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setDropdownOpen((o) => !o);
              }
            }}
            className={`CustomSelect ${dropdownOpen ? "Open" : ""} ${formError && !banco && !confirmModalFor ? "ErrorBorder" : ""}`}
            onClick={() => setDropdownOpen((o) => !o)}
          >
            <div className="CustomSelectValue" data-placeholder={!banco}>
              {!banco ? t.selectBank : selectedLabel}
            </div>
            <div className="CustomSelectChevron">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M6 9l6 6 6-6" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
          {dropdownOpen ? (
            <div className="CustomSelectDropdown">
              {BANCO_OPTIONS.map((opt) => (
                <div
                  key={opt.value}
                  className={`CustomSelectOption ${opt.value === banco ? "Selected" : ""}`}
                  onClick={() => {
                    setBanco(opt.value);
                    setNumeroCuenta("");
                    setNumeroCuenta2("");
                    setDropdownOpen(false);
                    setFormError("");
                  }}
                >
                  {opt.label}
                </div>
              ))}
            </div>
          ) : null}
        </div>
        <div className="PasswordInputWrapper">
          <input
            className={`PasswordInput ${!banco ? "Disabled" : ""} ${formError && !confirmModalFor ? "ErrorBorder" : ""}`}
            disabled={!banco}
            inputMode="numeric"
            placeholder={t.accountNumber}
            value={numeroCuenta}
            onChange={(e) => setNumeroCuenta(filtrarSoloNumeros(e.target.value))}
          />
        </div>
        <div className="PasswordInputWrapper">
          <input
            className={`PasswordInput ${!banco ? "Disabled" : ""}`}
            disabled={!banco}
            inputMode="numeric"
            placeholder={t.confirmAccountNumber}
            value={numeroCuenta2}
            onChange={(e) => setNumeroCuenta2(filtrarSoloNumeros(e.target.value))}
          />
        </div>
        {banco && (!formError || confirmModalFor) ? (
          <p style={{ fontSize: 12, color: "#666", textAlign: "center", margin: "0 0 8px" }}>{bankHelpText(banco, isEn)}</p>
        ) : null}
        {formError && !confirmModalFor ? <p className="ErrorText">{formError}</p> : null}
        <p style={{ fontSize: 12, color: "#999", textAlign: "center", margin: "0 0 12px" }}>{t.legalPersonal}</p>
        <div className="AjustesChangeActions">
          <button type="button" className="PrimaryButton" disabled={!canSubmit || !!confirmModalFor} onClick={requestSubmitAccount}>
            {submitting ? t.sending : t.confirmSend}
          </button>
        </div>
        <div className="HelpSection">
          <button type="button" className="WhatsAppHelpButton" onClick={openWhatsApp}>
            <span
              style={{
                backgroundColor: "#25D366",
                borderRadius: "50%",
                padding: 4,
                color: "#fff",
                display: "inline-flex",
              }}
            >
              <FaWhatsapp size={20} />
            </span>
            <span style={{ fontSize: 16, color: "#0f172a" }}>{t.needHelp}</span>
          </button>
        </div>
      </div>
    );
  };

  const rootClass = `PaymentCardsContainer AjustesChangeScreenRoot AjustesContainerTone${embedded ? " AjustesChangeScreenEmbed" : ""}`;

  return (
    <>
      <div className={rootClass}>
        <header className="PaymentCardsHeader">
          <button type="button" className="PaymentCardsBackButton" onClick={handleBack} aria-label="back">
            <FaAngleLeft size={24} />
          </button>
          <h1>{title}</h1>
        </header>
        <div className="PaymentCardsContent" style={contentGridStyle}>
          {renderBody()}
        </div>
      </div>
      {typeof document !== "undefined" && confirmModalFor
        ? createPortal(
            <div
              className="AjustesConfirmModalBackdrop"
              role="presentation"
              onClick={(e) => {
                if (e.target === e.currentTarget) closeConfirmModal();
              }}
            >
              <div
                className="AjustesConfirmModalPanel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="ajustes-confirm-title"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 id="ajustes-confirm-title">{t.modalTitle}</h2>
                <p className="AjustesConfirmModalBody">{confirmModalBody}</p>
                {formError ? <p className="AjustesConfirmModalError">{formError}</p> : null}
                <div className="AjustesConfirmModalActions">
                  <button type="button" className="PrimaryButton" disabled={submitting} onClick={onModalConfirm}>
                    {submitting ? t.sending : t.modalConfirmBtn}
                  </button>
                  <button type="button" className="AjustesLogoutConfirmCancel" disabled={submitting} onClick={closeConfirmModal}>
                    {t.modalCancel}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
